import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  User,
  Settings,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  AlertTriangle,
  Loader,
  Code,
  Play
} from 'lucide-react';
// Old SpinningSankalvaXLogo import - commented out
// import SpinningSankalvaXLogo from '../UI/SpinningSankalvaXLogo';
// New Gruve Logo imports
import GruveLogo from '../UI/GruveLogo';
import SpinningGruveLogo from '../UI/SpinningGruveLogo';
import PreBillCreationModal from '../BillProcessing/PreBillCreationModal';

const V2ChatMessages = ({ messages, isLoading, workflowName, error, runId, workflowId, token, sendMessage, draftBillData, resumeTrigger, chatService, setIsResuming, isResuming, executionStatus, currentStep, isReopenedSession = false, disableAutoOpen = false, agentId = null, returnPath = null }) => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [showBillModal, setShowBillModal] = useState(null);
  const showBillModalRef = useRef(showBillModal);
  const [recentlyConfirmed, setRecentlyConfirmed] = useState(false);
  const processedMessageIdsRef = useRef(new Set()); // Track processed message IDs
  const hasInitializedRef = useRef(false); // Track if we've initialized processed messages
  const initialMessageCountRef = useRef(0); // Track message count on first mount
  const [isProcessing, setIsProcessing] = useState(false); // Track if workflow is processing
  const [workflowContext, setWorkflowContext] = useState(null); // Store workflow context

  // Handle resume trigger - open PreBillCreationModal when Resume button clicked
  useEffect(() => {
    if (resumeTrigger > 0 && draftBillData) {
      console.log('🔄 Resume trigger activated, opening PreBillCreationModal with draft data:', draftBillData);

      // The spinning logo indicator is already showing (isResuming is true from button click)
      // Wait a moment for visual feedback, then open modal
      setTimeout(() => {
        // ✅ Direct access - backend sends FLAT, but handle legacy NESTED for backward compatibility
        let dataToOpen = draftBillData;
        if (draftBillData?.bill_data && !draftBillData?.line_items) {
          // Legacy NESTED format from old saved drafts - extract bill_data
          console.log('⚠️ Legacy NESTED format detected in draft, extracting bill_data');
          dataToOpen = draftBillData.bill_data;
        }
        setShowBillModal(dataToOpen);
        if (setIsResuming) {
          setIsResuming(false);
        }
      }, 800); // 800ms to show the loading state with spinning logo
    }
  }, [resumeTrigger, draftBillData, setIsResuming]);

  // Clear draft modal state when starting fresh (no draft data)
  useEffect(() => {
    if (!draftBillData && showBillModal && showBillModal.runId === draftBillData?.runId) {
      console.log('🧹 Clearing old draft modal state for fresh workflow');
      setShowBillModal(null);
    }
  }, [draftBillData]);

  // Fetch workflow context when modal is shown
  useEffect(() => {
    if (showBillModal && runId && token) {
      // ✅ Create abort controller to cancel fetch if component unmounts
      const abortController = new AbortController();

      console.log('🔄 Fetching workflow context for runId:', runId);
      fetch(`/api/v2/workflows/status/${runId}`, {
        signal: abortController.signal,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data.workflow_context) {
            console.log('✅ Workflow context fetched:', data.data.workflow_context);
            setWorkflowContext(data.data.workflow_context);
          }
        })
        .catch(err => {
          if (err.name === 'AbortError') {
            console.log('🚫 Workflow context fetch aborted');
          } else {
            console.error('❌ Error fetching workflow context:', err);
          }
        });

      // Cleanup: abort fetch on unmount
      return () => {
        abortController.abort();
      };
    }
  }, [showBillModal, runId, token]);

  console.log("📥 V2ChatMessages mounted with props:", {
    messagesCount: messages?.length,
    isLoading,
    hasError: !!error,
    runId,
    workflowId,
    hasSendMessage: !!sendMessage,
    isReopenedSession  // DEBUG: Log the flag value
  });


  // Keep ref in sync with state
  useEffect(() => {
    showBillModalRef.current = showBillModal;
  }, [showBillModal]);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 🔧 REOPENED SESSION CHECK: Only block modal auto-open if this is a reopened session
  // Fresh workflows in same session will have isReopenedSession=false
  useEffect(() => {
    if (hasInitializedRef.current) return; // Only run once on mount

    console.log("🔧 INIT useEffect triggered:", {
      hasInitialized: hasInitializedRef.current,
      messagesLength: messages?.length,
      isReopenedSession
    });

    // CRITICAL: Mark as initialized IMMEDIATELY to prevent running on subsequent renders
    hasInitializedRef.current = true;

    // Only mark existing messages as processed if this is a reopened session with messages
    if (messages && messages.length > 0 && isReopenedSession) {
      console.log("🔧 INIT: Reopened session detected - marking ALL existing messages as processed");
      console.log("🔧 INIT: Total messages to mark:", messages.length);

      // Mark ALL messages as processed to prevent ANY modal from auto-opening
      messages.forEach(msg => {
        processedMessageIdsRef.current.add(msg.id);
      });

      console.log("🔧 INIT: Marked messages:", Array.from(processedMessageIdsRef.current));
    } else {
      console.log("🔧 INIT: Fresh workflow or no messages - allowing normal modal behavior");
    }
  }, [messages, isReopenedSession]);

  // Check for bill confirmation triggers and successful bill creation in messages
  // PERFORMANCE FIX: Only process NEW messages, not all messages every time
  useEffect(() => {
    if (!messages || messages.length === 0) return;

    console.log("🔍 MAIN useEffect triggered:", {
      totalMessages: messages.length,
      processedCount: processedMessageIdsRef.current.size,
      processedIds: Array.from(processedMessageIdsRef.current)
    });

    // CRITICAL FIX: Only process messages we haven't seen before
    const newMessages = messages.filter(msg => !processedMessageIdsRef.current.has(msg.id));

    if (newMessages.length === 0) {
      console.log("✅ No new messages to process, skipping useEffect");
      return;
    }

    console.log("🔍 Processing", newMessages.length, "NEW messages (total:", messages.length, ")");
    console.log("🔍 New message IDs:", newMessages.map(m => m.id));

    // PERFORMANCE FIX: Only loop through NEW messages, not all messages
    for (let i = newMessages.length - 1; i >= 0; i--) {
      const message = newMessages[i];

      // Mark this message as processed
      processedMessageIdsRef.current.add(message.id);
      console.log(`🔍 Message ${i}:`, {
        id: message?.id,
        type: message?.type,
        contentType: typeof message?.content,
        contentLength: message?.content?.length,
        contentPreview: typeof message?.content === 'string'
          ? message.content.substring(0, 100) + '...'
          : message?.content
      });

      // 🔧 Calculate if this is the current step awaiting user input (used for disableAutoOpen exception)
      const isCurrentAwaitingStep = (
        executionStatus === 'awaiting_user' &&
        currentStep &&
        message.step_name === currentStep
      );

      // 🔧 CRITICAL FIX: Also process workflow_step messages (they contain component_definition with full data)
      if (message && (message.type === 'assistant' || message.type === 'workflow_step' || message.type === 'workflow')) {
        console.log(`🔍 Processing ${message.type} message:`, message.id);
        console.log("🔍 Message has isUIComponent:", message.isUIComponent);
        console.log("🔍 Message content type:", typeof message.content);

        let parsedContent = null;

        // Case 1: UI Component message from V2 workflow system
        if (message.isUIComponent && typeof message.content === 'object') {
          console.log("✅ UI Component message detected");
          parsedContent = message.content;
        }
        // Case 2: content is already an object (backend parsed JSON)
        else if (typeof message.content === 'object' && message.content !== null) {
          console.log("✅ Content is already an object");
          parsedContent = message.content;
        }

        // Case 2: content is a string that might contain JSON
        if (typeof message.content === 'string') {
          console.log("🔍 Content is string, attempting multiple parse strategies");
          const trimmed = message.content.trim();

          // ✅ FIX: Sanitize JSON string by removing control characters (DEL, etc.)
          // This handles corrupted JSON like: "Jabra Evolve 30 II MS\u000546\u007f\u007f..."
          const sanitizeJson = (str) => {
            // Remove control characters: \u0000-\u001F (C0 controls) and \u007F-\u009F (DEL + C1 controls)
            return str.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
          };

          // Strategy 1: Direct JSON parsing (original)
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
              const sanitized = sanitizeJson(trimmed);
              parsedContent = JSON.parse(sanitized);
              console.log("✅ Successfully parsed direct JSON:", Object.keys(parsedContent || {}));
            } catch (e) {
              console.log("❌ Failed to parse direct JSON:", e.message);
            }
          }

          // Strategy 2: Look for JSON after descriptive text (like "Bill confirmed for creation with the following data: {JSON}")
          if (!parsedContent) {
            console.log("🔍 Attempting to extract JSON from descriptive text");
            try {
              // Pattern: "Some descriptive text: {json_data}"
              const colonPos = trimmed.indexOf(': {');
              if (colonPos !== -1) {
                const jsonPart = trimmed.substring(colonPos + 2); // Skip ': '
                const sanitized = sanitizeJson(jsonPart);
                parsedContent = JSON.parse(sanitized);
                console.log("✅ Successfully extracted JSON after colon:", Object.keys(parsedContent || {}));
              } else {
                // Look for standalone JSON object in the message using regex
                const jsonMatch = trimmed.match(/\{.*\}/s);
                if (jsonMatch) {
                  const jsonPart = jsonMatch[0];
                  const sanitized = sanitizeJson(jsonPart);
                  parsedContent = JSON.parse(sanitized);
                  console.log("✅ Successfully extracted JSON using regex:", Object.keys(parsedContent || {}));
                }
              }
            } catch (e) {
              console.log("❌ Failed to extract JSON from descriptive text:", e.message);
              console.log("❌ Content that failed:", trimmed.substring(0, 200));
            }
          }

          if (!parsedContent) {
            console.log("❌ No JSON found in string content");
          }
        }

        // 🚫 Prevent modal reopen immediately after user confirms
        if (recentlyConfirmed) {
          // ✅ If this is a modal reopen attempt → skip just that part
          if (message.isUIComponent || (parsedContent && parsedContent.component_definition)) {
            console.log("🚫 Skipping modal reopen (recently confirmed)");
            continue; // skip only modal-related content
          }
          // ✅ For plain assistant messages, let them flow to rendering
        }



        // Check parsed content for confirmation triggers
        if (parsedContent) {
          console.log("🔍 Parsed content keys:", Object.keys(parsedContent));
          console.log("🔍 next_action_required:", parsedContent.next_action_required);

          // NEW: Check for UI component definition (from UI steps)
          if (parsedContent.component_definition) {
            const componentDef = parsedContent.component_definition;
            console.log("🎯 FOUND UI COMPONENT DEFINITION:", componentDef);
            console.log("🎯 Component metadata:", componentDef.metadata);
            console.log("🎯 Component metadata name:", componentDef.metadata?.name);

            // Check if it's a modal component (PreBillCreationModal, UnmatchedItemsResolver, etc.)
            const modalComponents = ["PreBillCreationModal", "UnmatchedItemsResolver"];
            console.log("🎯 Checking if component name matches modal components:", modalComponents);
            if (componentDef.metadata && modalComponents.includes(componentDef.metadata.name)) {
              console.log("🎯 FOUND MODAL COMPONENT:", componentDef.metadata.name, "Opening modal with bill data:", componentDef.bill_data);
              console.log("🎯 Message ID:", message.id);
              console.log("🎯 Message step_name:", message.step_name);
              console.log("🎯 Current workflow step:", currentStep);
              console.log("🎯 Execution status:", executionStatus);
              console.log("🎯 Is message already processed?", processedMessageIdsRef.current.has(message.id));
              console.log("🎯 Is reopened session?", isReopenedSession);

              // 🔧 FIX: Don't show modal if workflow is already completed
              if (executionStatus === 'completed') {
                console.log("🚫 Workflow already completed - NOT showing modal (prevents reopen on browser back)");
                continue; // Skip showing modal
              }

              // 🔧 CRITICAL FIX: Smart modal reopening logic
              // If message was already processed, only skip if:
              // 1. It's NOT the current active step, OR
              // 2. Workflow is NOT awaiting user input
              // (isCurrentAwaitingStep is already defined at top of loop)

              if (processedMessageIdsRef.current.has(message.id)) {
                if (isCurrentAwaitingStep) {
                  console.log("✅ REOPENING: Workflow is actively awaiting user input at this step - reopening modal");
                } else {
                  console.log("🚫 SKIPPING: Message already processed and not current awaiting step - not reopening modal");
                  return;
                }
              }

              // 🔧 CRITICAL FIX: Before opening modal, check if bill was already created
              const billAlreadyCreated = messages.some(msg => {
                try {
                  let content = msg.content;
                  if (typeof content === 'string') {
                    const jsonMatch = content.match(/\{.*\}/s);
                    if (jsonMatch) {
                      content = JSON.parse(jsonMatch[0]);
                    } else {
                      return content.includes('Bill created successfully') ||
                             content.includes('Internal ID:') ||
                             content.includes('bill_creation_successful');
                    }
                  }
                  if (typeof content === 'object' && content !== null) {
                    return content.next_action_required === 'bill_creation_successful' ||
                           (content.message_for_user && content.message_for_user.includes('Bill created successfully'));
                  }
                } catch (e) {
                  return false;
                }
                return false;
              });

              if (billAlreadyCreated) {
                console.log("🚫 SKIPPING: Bill already created - not reopening modal component");
                return;
              }

              // 🔧 CRITICAL FIX: Don't auto-open modal if this is a resumed draft session
              // In that case, modal should only open when user clicks "Resume" button
              if (draftBillData) {
                console.log("⏸️ Draft session detected - skipping auto-open, waiting for Resume button click");
                return;
              }

              if (!showBillModal && !recentlyConfirmed) {
                // 🔧 Check if auto-open is allowed (disabled for reopened threads, but allow if workflow awaiting input)
                // Also prevent reopening if user just confirmed (recentlyConfirmed flag)
                if (!disableAutoOpen || isCurrentAwaitingStep) {
                  // ✅ Handle BOTH FLAT and NESTED formats
                  // NESTED: {"bill_data": {...}} - extract bill_data
                  // FLAT: {...} - use as is
                  let dataToOpen = componentDef;
                  if (componentDef.bill_data && !componentDef.line_items) {
                    console.log("⚠️ NESTED format detected in component_definition - extracting bill_data");
                    dataToOpen = componentDef.bill_data;
                  }

                  console.log("🎯 Setting showBillModal state with data:", dataToOpen);
                  console.log("🎯 THIS WILL OPEN THE MODAL");

                  // 🔍 DEBUG: Log line items structure when setting modal data
                  if (dataToOpen && dataToOpen.line_items) {
                    console.log(`🔍 SET-MODAL: Data has ${dataToOpen.line_items.length} line items`);
                    dataToOpen.line_items.slice(0, 2).forEach((item, idx) => {
                      console.log(`🔍 SET-MODAL Line ${idx + 1}:`, {
                        keys: Object.keys(item || {}),
                        has_available_items: !!item?.available_items,
                        available_items_count: item?.available_items ? item.available_items.length : 0,
                        has_matched: !!item?.matched_netsuite_item,
                        description: item?.description
                      });
                    });
                  }

                  setShowBillModal(dataToOpen);
                  return;
                } else {
                  console.log("🚫 SKIPPING: Auto-open disabled for reopened thread (use Review button)");
                }
              } else {
                console.log("⚠️ Modal already open, not opening again");
              }
            } else {
              console.log("❌ Component name does not match modal components or no metadata found");
            }
          }

          // Check for specific modal trigger actions (these come FROM AI steps TO trigger the modal)
          // ✅ CRITICAL FIX: Only open modal for SPECIFIC confirmation actions, not routing actions
          // This list should match userConfirmationActions below to avoid duplicate logic
          const modalTriggers = [
            // REMOVED: "confirm_bill_creation" - this is a routing action between AI steps
            // Modal should only open when UI step (standalone_bill_confirmation_step) sends component_definition
            "proceed_to_create_standalone_bill",
            "request_user_confirmation",  // Only when explicitly requesting confirmation
            "unmatched_items_found"
            // REMOVED: "create_bill_from_purchase_order" - this is a tool action, not a confirmation
            // REMOVED: "standalone_bill_confirmation" - this is a step name, not an action
            // REMOVED: "bill_confirmation_required" - not used in current workflow
          ];

          if (modalTriggers.includes(parsedContent.next_action_required)) {
            console.log("🎯 FOUND TRIGGER! next_action_required:", parsedContent.next_action_required);

            // 🔧 CRITICAL FIX: Before opening modal, check if bill was already created
            // Don't reopen modal if there's a completion message AFTER this confirmation trigger
            const billAlreadyCreated = messages.some(msg => {
              try {
                let content = msg.content;
                if (typeof content === 'string') {
                  // Try to parse JSON
                  const jsonMatch = content.match(/\{.*\}/s);
                  if (jsonMatch) {
                    content = JSON.parse(jsonMatch[0]);
                  } else {
                    // Check for success keywords in plain text
                    return content.includes('Bill created successfully') ||
                           content.includes('Internal ID:') ||
                           content.includes('bill_creation_successful');
                  }
                }

                // Check for bill_creation_successful in parsed content
                if (typeof content === 'object' && content !== null) {
                  return content.next_action_required === 'bill_creation_successful' ||
                         (content.message_for_user && content.message_for_user.includes('Bill created successfully'));
                }
              } catch (e) {
                return false;
              }
              return false;
            });

            if (billAlreadyCreated) {
              console.log("🚫 SKIPPING: Bill already created - not reopening confirmation modal");
              return;
            }

            // 🔧 CRITICAL FIX: Don't auto-open modal if this is a resumed draft session
            // In that case, modal should only open when user clicks "Resume" button
            if (draftBillData) {
              console.log("⏸️ Draft session detected - skipping auto-open, waiting for Resume button click");
              return;
            }

            if (!showBillModal && !recentlyConfirmed) {
              // 🔧 Check if auto-open is allowed (disabled for reopened threads, but allow if workflow awaiting input)
              // Also prevent reopening if user just confirmed (recentlyConfirmed flag)
              if (!disableAutoOpen || isCurrentAwaitingStep) {
                // ✅ Handle BOTH FLAT and NESTED formats
                let dataToOpen = parsedContent;
                if (parsedContent.bill_data && !parsedContent.line_items) {
                  console.log("⚠️ NESTED format detected - extracting bill_data");
                  dataToOpen = parsedContent.bill_data;
                }
                console.log("🎯 Opening bill confirmation modal with data:", dataToOpen);
                setShowBillModal(dataToOpen);
                return;
              } else {
                console.log("🚫 SKIPPING: Auto-open disabled for reopened thread (use Review button)");
              }
            } else {
              console.log("⚠️ Modal already open, not opening again");
            }
          }

          // Additional check for any message containing bill-related data that needs confirmation
          const hasBillData = parsedContent.bill_data ||
            (parsedContent.vendor && parsedContent.line_items) ||
            (parsedContent.vendor && parsedContent.total_amount);

          if (hasBillData) {
            console.log("🔍 Found potential bill data in message:", Object.keys(parsedContent));

            // DUPLICATE MODAL FIX: Skip user confirmation messages that we just sent
            const isUserConfirmationMessage = message.type === 'user' &&
              typeof message.content === 'string' &&
              message.content.includes('Bill confirmed for creation with the following data:');

            if (isUserConfirmationMessage) {
              console.log("🚫 SKIPPING: This is a user confirmation message we just sent, not reopening modal");
              return;
            }

            // ADDITIONAL FIX: Skip reopening modal if we recently confirmed (prevents race condition)
            if (recentlyConfirmed) {
              // ✅ If this is a modal reopen attempt → skip just that part
              if (message.isUIComponent || (parsedContent && parsedContent.component_definition)) {
                console.log("🚫 Skipping modal reopen (recently confirmed)");
                continue; // skip only modal-related content
              }
              // ✅ For plain assistant messages, let them flow to rendering
            }




            // ENHANCED DUPLICATE MODAL FIX: Don't reopen modal if we just created a bill
            // This prevents the completion step from reopening the modal after bill creation
            if (parsedContent.component_definition &&
              parsedContent.component_definition.metadata &&
              parsedContent.component_definition.metadata.name === 'PreBillCreationModal') {

              // Check if we might be in final_output_step by looking for completion indicators
              const hasCompletionIndicators = messages.some(msg => {
                if (typeof msg.content === 'string') {
                  return msg.content.includes('Bill created successfully') ||
                    msg.content.includes('Internal ID:') ||
                    msg.content.includes('NetSuite Bill ID:') ||
                    msg.content.includes('bill_creation_successful');
                }
                return false;
              });

              // Also check for recent bill creation in tool results
              const hasRecentBillCreation = messages.slice(-5).some(msg => {
                if (msg.role === 'tool' && typeof msg.content === 'string') {
                  try {
                    const toolResult = JSON.parse(msg.content);
                    return toolResult.message_for_user &&
                      toolResult.message_for_user.includes('Bill created successfully');
                  } catch (e) {
                    return false;
                  }
                }
                return false;
              });

              if (hasCompletionIndicators || hasRecentBillCreation) {
                console.log("🚫 SKIPPING: Bill creation completed, preventing duplicate modal reopening");
                console.log("Completion indicators:", hasCompletionIndicators, "Recent creation:", hasRecentBillCreation);
                return;
              }
            }

            // Check if this looks like it needs user confirmation
            // ✅ CRITICAL FIX: Modal should ONLY open from UI step component_definition
            // REMOVED confirm_bill_creation - it's a routing action between AI steps
            const userConfirmationActions = [
              'proceed_to_create_standalone_bill',
              'request_user_confirmation',
              'unmatched_items_found'
            ];

            const needsConfirmation = (
              parsedContent.next_action_required &&
              userConfirmationActions.includes(parsedContent.next_action_required)
            );
            // REMOVED: String-based "confirm" check - too aggressive, triggers on any message mentioning confirmation
            // Old check: || (typeof message.content === 'string' && message.content.toLowerCase().includes('confirm'))

            // ✅ FIX: DISABLED - Modal should ONLY open from UI step component_definition
            // Not from AI step bill_data - this was causing modal to open before fetch_bill_defaults_step
            const hasCompleteBillData = false;

            if (!showBillModal && !recentlyConfirmed && (needsConfirmation || hasCompleteBillData)) {
              // 🔧 CRITICAL FIX: Before opening modal, check if bill was already created
              // Also prevent reopening if user just confirmed (recentlyConfirmed flag)
              const billAlreadyCreated = messages.some(msg => {
                try {
                  let content = msg.content;
                  if (typeof content === 'string') {
                    const jsonMatch = content.match(/\{.*\}/s);
                    if (jsonMatch) {
                      content = JSON.parse(jsonMatch[0]);
                    } else {
                      return content.includes('Bill created successfully') ||
                             content.includes('Internal ID:') ||
                             content.includes('bill_creation_successful');
                    }
                  }
                  if (typeof content === 'object' && content !== null) {
                    return content.next_action_required === 'bill_creation_successful' ||
                           (content.message_for_user && content.message_for_user.includes('Bill created successfully'));
                  }
                } catch (e) {
                  return false;
                }
                return false;
              });

              if (billAlreadyCreated) {
                console.log("🚫 SKIPPING: Bill already created - not reopening modal from bill_data detection");
                return;
              }

              // 🔧 CRITICAL FIX: Don't auto-open modal if this is a resumed draft session
              if (draftBillData) {
                console.log("⏸️ Draft session detected - skipping auto-open, waiting for Resume button click");
                return;
              }

              // 🔧 Check if auto-open is allowed (disabled for reopened threads, but allow if workflow awaiting input)
              if (!disableAutoOpen || isCurrentAwaitingStep) {
                // ✅ Handle BOTH FLAT and NESTED formats
                let dataToOpen = parsedContent;
                if (parsedContent.bill_data && !parsedContent.line_items) {
                  console.log("⚠️ NESTED format detected - extracting bill_data");
                  dataToOpen = parsedContent.bill_data;
                }
                console.log("🎯 Opening bill modal from bill_data detection. Action:", parsedContent.next_action_required);
                setShowBillModal(dataToOpen);
                return;
              } else {
                console.log("🚫 SKIPPING: Auto-open disabled for reopened thread (use Review button)");
              }
            }
          }
        }

        // Fallback: Check for confirmation keywords in string content
        if (!parsedContent && typeof message.content === 'string') {
          const content = message.content.toLowerCase();
          const confirmationKeywords = [
            'confirm bill creation',
            'confirm the bill',
            'proceed with bill creation',
            'ready to create bill',
            'bill confirmation required'
          ];

          if (confirmationKeywords.some(keyword => content.includes(keyword))) {
            console.log("🎯 Found confirmation keyword in text, checking for bill data");

            // Try to extract any JSON data from the message for the modal
            try {
              const jsonMatch = typeof message.content === 'string' ? message.content.match(/\{.*\}/s) : null;
              if (jsonMatch && !showBillModal && !recentlyConfirmed) {
                // 🔧 CRITICAL FIX: Don't auto-open modal if this is a resumed draft session
                if (draftBillData) {
                  console.log("⏸️ Draft session detected - skipping auto-open, waiting for Resume button click");
                  return;
                }

                // 🔧 Check if auto-open is allowed (disabled for reopened threads, but allow if workflow awaiting input)
                if (!disableAutoOpen || isCurrentAwaitingStep) {
                  const extractedData = JSON.parse(jsonMatch[0]);
                  // ✅ Handle BOTH FLAT and NESTED formats
                  let dataToOpen = extractedData;
                  if (extractedData.bill_data && !extractedData.line_items) {
                    console.log("⚠️ NESTED format detected - extracting bill_data");
                    dataToOpen = extractedData.bill_data;
                  }
                  console.log("🎯 Opening modal with extracted data from confirmation text");
                  setShowBillModal(dataToOpen);
                  return;
                } else {
                  console.log("🚫 SKIPPING: Auto-open disabled for reopened thread (use Review button)");
                }
              }
            } catch (e) {
              console.log("❌ Could not extract bill data from confirmation message");
            }
          }
        }

        // ⚠️ AUTO-CLOSE DISABLED: Modal now handles its own lifecycle internally
        // The PreBillCreationModal calls the workflow API directly and shows success/error states
        // It closes itself when the user clicks "Cancel" or explicitly dismisses it
        // Keeping this code commented for reference but auto-close is disabled
        /*
        // Check for workflow completion (success OR failure) and auto-close modal
        // ONLY auto-close if user has already confirmed (recentlyConfirmed = true)
        if (showBillModal && recentlyConfirmed && (message.type === 'assistant' || message.type === 'tool_result')) {
          let shouldCloseModal = false;
          let closeDelay = 3000; // Default 3 seconds
          let logMessage = "";

          // Check for successful bill creation
          if (typeof message.content === 'string') {
            const successPatterns = [
              /(?:Bill.*created|Bill.*successfully|NetSuite.*Bill.*ID)/i,
              /Successfully created Vendor Bill/i,
              /netsuite_bill_id.*\d+/i,
              /status.*success.*created/i,
              /Workflow completed successfully/i
            ];

            const isSuccessMessage = successPatterns.some(pattern => message.content.match(pattern));
            if (isSuccessMessage) {
              shouldCloseModal = true;
              logMessage = "🎉 SUCCESS: Detected successful bill creation or workflow completion";
            }
          }

          // Check for error/failure messages (multiple patterns)
          if (typeof message.content === 'string') {
            const errorPatterns = [
              /bill.*creation.*failed/i,
              /bill.*failed/i,
              /error.*creating.*bill/i,
              /netsuite.*api.*error/i,
              /purchase.*order.*not.*found/i,
              /unable.*to.*proceed/i,
              /connectivity.*issue/i,
              /unknown.*error/i,
              /try.*again.*later/i,
              /contact.*support/i
            ];

            const hasError = errorPatterns.some(pattern => message.content.match(pattern));
            if (hasError) {
              shouldCloseModal = true;
              closeDelay = 5000; // Give more time to read error message
              logMessage = "❌ ERROR: Detected bill creation error message";
            }
          }

          // Check for JSON error responses
          if (typeof message.content === 'object' || (typeof message.content === 'string' && message.content.includes('{"error"'))) {
            try {
              const parsedContent = typeof message.content === 'object' ? message.content : JSON.parse(message.content);
              if (parsedContent.error || parsedContent.message_for_user?.includes('failed') || parsedContent.next_action_required === 'bill_creation_failed') {
                shouldCloseModal = true;
                closeDelay = 5000; // Give more time to read error message
                logMessage = "❌ ERROR: Detected JSON error response";
              }
            } catch (e) {
              // Not valid JSON, ignore
            }
          }

          // Check for workflow completion steps that indicate end of process
          const completionSteps = [
            'final_output_step',
            'halt_workflow_bill_creation_error',
            'halt_workflow_ai_error_step',
            'halt_workflow_netsuite_search_error_step'
          ];

          if (message.metadata?.step_name && completionSteps.includes(message.metadata.step_name)) {
            shouldCloseModal = true;
            closeDelay = 4000;
            logMessage = `🏁 WORKFLOW COMPLETED: Detected completion step ${message.metadata.step_name}`;
          }

          // Auto-close modal if any completion condition is met AND user has already confirmed
          if (shouldCloseModal && showBillModal) {
            console.log(`${logMessage}, auto-closing modal in ${closeDelay / 1000}s (user already confirmed)`);
            setTimeout(() => {
              console.log("🔒 AUTO-CLOSE: Checking if modal is still open before closing");
              console.log("🔒 AUTO-CLOSE: Current showBillModal state:", !!showBillModalRef.current);
              if (showBillModalRef.current) {
                console.log("🔒 AUTO-CLOSE: Modal still open, closing now");
                setShowBillModal(null);
                setRecentlyConfirmed(false); // reset flag here after workflow finishes
              } else {
                console.log("🔒 AUTO-CLOSE: Modal already closed by user action, skipping auto-close");
              }
            }, closeDelay);
            return; // Don't check other messages once we find completion
          }
        }
        */
      }
    }
  }, [messages, showBillModal, recentlyConfirmed, executionStatus, currentStep]);


  const getMessageIcon = (type) => {
    switch (type) {
      case 'user':
        return <User className="w-4 h-4" />;
      case 'assistant':
        return <Bot className="w-4 h-4" />;
      case 'system':
        return <Settings className="w-4 h-4" />;
      case 'error':
        return <XCircle className="w-4 h-4" />;
      case 'tool_call':
        return <Zap className="w-4 h-4" />;
      case 'tool_result':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Settings className="w-4 h-4" />;
    }
  };

  const getMessageStyles = (type) => {
    switch (type) {
      case 'user':
        return {
          container: 'justify-end',
          bubble: 'bg-purple-600 text-white',
          icon: 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
        };
      case 'assistant':
        return {
          container: 'justify-start',
          bubble: 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700',
          icon: 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
        };
      case 'system':
        return {
          container: 'justify-center',
          bubble: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700',
          icon: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        };
      case 'error':
        return {
          container: 'justify-start',
          bubble: 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800',
          icon: 'bg-red-100 text-red-600 dark:text-red-900/20 dark:text-red-400'
        };
      case 'tool_call':
        return {
          container: 'justify-start',
          bubble: 'bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200 border border-orange-200 dark:border-orange-800',
          icon: 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400'
        };
      case 'tool_result':
        return {
          container: 'justify-start',
          bubble: 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800',
          icon: 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400'
        };
      default:
        return {
          container: 'justify-start',
          bubble: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
          icon: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        };
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderMessageContent = (content, type, message) => {
    if (type === 'tool_call' || type === 'tool_result') {
      return renderToolMessage(content, type);
    }

    // UI component messages
    if (message && message.isUIComponent && typeof content === 'object' && content.component_definition) {
      return (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="text-blue-800 dark:text-blue-200 font-medium">
            {content.message || 'UI component ready for interaction'}
          </div>
        </div>
      );
    }


    // Object content (including success messages wrapped in message_for_user)
    if (typeof content === 'object' && content !== null) {
      if (content.message_for_user) {
        const msg = content.message_for_user;
        return <div className="whitespace-pre-wrap break-words">{msg}</div>;
      }

      return (
        <div className="whitespace-pre-wrap break-words">
          {JSON.stringify(content, null, 2)}
        </div>
      );
    }


    // String content with highlights
    // String content with highlights
    if (typeof content === 'string') {
      return (
        <div className="whitespace-pre-wrap break-words">
          {content}
        </div>
      );
    }


    return null;
  };


  // Bill confirmation modal handlers
  const handleConfirmBill = async (finalBillData) => {
    try {
      console.log('🚀 V2ChatMessages handleConfirmBill called with:', finalBillData);
      console.log('🚀 runId from props:', runId);
      console.log('🚀 workflowId from props:', workflowId);

      // Try to get runId and workflowId from multiple sources (props or bill data)
      // This handles cases where props might be temporarily undefined after idle time
      const effectiveRunId = runId || finalBillData?.bill_data?.run_id || finalBillData?.run_id;
      const effectiveWorkflowId = workflowId || finalBillData?.bill_data?.workflow_id || finalBillData?.workflow_id;

      console.log('🚀 effectiveRunId (from props or bill data):', effectiveRunId);
      console.log('🚀 effectiveWorkflowId (from props or bill data):', effectiveWorkflowId);

      if (!effectiveRunId) {
        console.error('🚀 ERROR: No workflow run ID available from any source');
        throw new Error('No workflow run ID available. Please try reopening the workflow.');
      }

      if (!effectiveWorkflowId) {
        console.warn('⚠️ WARNING: No workflow ID available from props, but continuing with runId');
        // Don't throw error for workflowId - it's less critical than runId
      }

      // Close modal immediately
      setShowBillModal(null);

      // Set recently confirmed flag to prevent modal reopening
      setRecentlyConfirmed(true);

      // Show processing indicator ONLY for resume workflows (purple logo)
      // For live workflows, keep using isLoading (blue logo)
      if (draftBillData) {
        // This is a resumed draft workflow - show purple processing indicator
        setIsProcessing(true);
        console.log('🟣 Resume workflow: Showing purple processing indicator');
      } else {
        // This is a live workflow - blue loading indicator will continue showing
        console.log('🔵 Live workflow: Keeping blue loading indicator');
      }

      // Call workflow API to continue execution with bill data
      // Use the existing /continue endpoint with properly formatted user_message

      console.log('🚀 Calling workflow API to continue with bill creation');
      console.log('🚀 finalBillData:', finalBillData);

      // Strip out large arrays to prevent 413 Content Too Large error
      const {
        all_netsuite_items,
        original_pdf_line_items,
        available_vendors,
        ...billDataWithoutLargeArrays
      } = finalBillData;

      // Also strip out large arrays from nested bill_data if it exists
      if (billDataWithoutLargeArrays.bill_data) {
        const {
          all_netsuite_items: nestedItems,
          original_pdf_line_items: nestedPdfItems,
          available_vendors: nestedVendors,
          ...cleanBillData
        } = billDataWithoutLargeArrays.bill_data;
        billDataWithoutLargeArrays.bill_data = cleanBillData;
      }

      // Also strip out large arrays from po_data if it exists
      if (billDataWithoutLargeArrays.po_data) {
        const { inventory_assignments, ...cleanPoData } = billDataWithoutLargeArrays.po_data;
        billDataWithoutLargeArrays.po_data = cleanPoData;
      }

      console.log('🚀 Stripped large arrays from bill data, payload size reduced');

      // Include BOTH next_action_required AND bill_data so backend knows which step to transition to
      const userMessage = `Bill confirmed for creation with the following data: ${JSON.stringify(billDataWithoutLargeArrays)}`;

      console.log('🌐 Making fetch request to /api/v2/workflows/continue/', effectiveRunId);
      console.log('🔑 Using token (first 20 chars):', token.substring(0, 20) + '...');

      const response = await fetch(`/api/v2/workflows/continue/${effectiveRunId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_message: userMessage,
          token: token
        })
      });

      console.log('📡 Fetch response received - status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API error response:', errorText);
        throw new Error(`Workflow continuation failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      console.log('✅ Bill creation workflow continued successfully - polling for new messages...');

      // ✅ CRITICAL FIX: After long idle time (5-6 mins), force refresh workflow status
      // This ensures we get latest state even if polling misses events
      try {
        // Force fetch latest workflow status to ensure we don't miss completion
        const statusResponse = await fetch(`/api/v2/workflows/status/${effectiveRunId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          console.log('✅ Forced workflow status refresh:', statusData.status);
        }
      } catch (statusError) {
        console.warn('⚠️ Could not force refresh status:', statusError);
      }

      // 🔧 CRITICAL FIX: Get CURRENT message count from backend before polling
      // If modal was open for 5-6 minutes, React state might be stale
      let currentMessageCount = messages.length;
      try {
        const sessionId = await chatService.getSessionIdFromRunId(effectiveRunId);
        if (sessionId) {
          const currentMessages = await chatService.loadSession(sessionId);
          currentMessageCount = currentMessages.length;
          console.log(`🔄 Refreshed message count: ${messages.length} (stale) -> ${currentMessageCount} (current)`);
        }
      } catch (refreshError) {
        console.warn('⚠️ Could not refresh message count, using stale value:', refreshError);
      }

      // 🔧 CRITICAL FIX FOR RESUMED WORKFLOWS:
      // After clicking "Create" in a resumed workflow, the backend processes the bill
      // but the UI doesn't update because the hook's polling may have stopped
      // Solution: Force a page reload to restart the hook with fresh state

      console.log('✅ Workflow continuation request sent');
      console.log('🔄 Reloading page to show new messages...');

      // Hide processing indicator first
      setIsProcessing(false);

      // Force page reload after a short delay to show the processing state briefly
      setTimeout(() => {
        console.log('🔄 Reloading page to refresh workflow state and messages');
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error confirming bill:', error);
      setIsProcessing(false); // Hide processing indicator on error
      alert('Error confirming bill: ' + error.message);
    }
  };

  // ❌ Handle X button click - just close modal without saving draft
  const handleCloseBill = (modifiedData) => {
    console.log('❌ V2ChatMessages handleCloseBill called - Just closing modal, no draft save');
    console.log('📍 User stays on V2Chat page');

    // ⚠️ CRITICAL FIX: Update showBillModal with modified data even when just closing
    // This preserves user modifications if they reopen the modal
    if (modifiedData) {
      console.log('💾 Received modified bill data from modal - updating state');
      setShowBillModal(modifiedData);
      // Wait a moment for state to update, then close
      setTimeout(() => setShowBillModal(null), 50);
    } else {
      setShowBillModal(null); // Just hide modal, stay on current page
    }
  };

  const handleCancelBill = async (modifiedData) => {
    console.log('🚫 V2ChatMessages handleCancelBill called - Bill creation cancelled');

    // ⚠️ CRITICAL FIX: Use modified data from modal instead of stale showBillModal
    const billDataToSave = modifiedData || showBillModal;
    console.log('📋 Saving bill data as draft:', billDataToSave);
    if (modifiedData) {
      console.log('💾 Using MODIFIED data from modal (includes user changes)');
    } else {
      console.log('⚠️ Using original showBillModal data (no modifications)');
    }

    // 🔧 CRITICAL FIX: Don't save as draft if bill was already created
    // Check if there's a bill_creation_successful message in chat history
    const billAlreadyCreated = messages.some(msg => {
      try {
        let content = msg.content;
        if (typeof content === 'string') {
          const jsonMatch = content.match(/\{.*\}/s);
          if (jsonMatch) {
            content = JSON.parse(jsonMatch[0]);
          } else {
            // ✅ Enhanced: Also check for netsuite_url in string content
            return content.includes('Bill created successfully') ||
                   content.includes('Internal ID:') ||
                   content.includes('bill_creation_successful') ||
                   content.includes('netsuite_url') ||
                   content.includes('.app.netsuite.com');
          }
        }
        if (typeof content === 'object' && content !== null) {
          // ✅ Enhanced: Also check for netsuite_url field
          return content.next_action_required === 'bill_creation_successful' ||
                 content.netsuite_url ||
                 content.bill_url ||
                 (content.message_for_user && content.message_for_user.includes('Bill created successfully'));
        }
      } catch (e) {
        return false;
      }
      return false;
    });

    if (billAlreadyCreated) {
      console.log('🚫 CANCEL BLOCKED: Bill already created - not saving as draft');
      console.log('✅ Simply closing modal without saving draft');
      setShowBillModal(null);
      return;
    }

    // 🔍 DEBUG: Log line items structure
    if (billDataToSave && billDataToSave.line_items) {
      console.log(`🔍 CANCEL: billDataToSave has ${billDataToSave.line_items.length} line items`);
      billDataToSave.line_items.slice(0, 2).forEach((item, idx) => {
        console.log(`🔍 CANCEL Line ${idx + 1}:`, {
          keys: Object.keys(item),
          has_available_items: !!item.available_items,
          available_items_count: item.available_items ? item.available_items.length : 0,
          has_matched: !!item.matched_netsuite_item,
          description: item.description
        });
      });
    }

    // Save the bill data as draft
    try {
      // Save draft with the current bill data AND runId
      // Strip out large data that can be refetched (all_netsuite_items)
      const { all_netsuite_items, ...billDataWithoutLargeArrays } = billDataToSave;

      const draftDataToSave = {
        ...billDataWithoutLargeArrays,
        runId: runId // Include runId so it can be used when resuming
      };

      const saveDraftResponse = await fetch(`/api/chat/save-draft`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          workflow_id: workflowId,
          run_id: runId,
          draft_data: draftDataToSave, // Save bill data with runId included
          token: token
        })
      });

      // Determine redirect path - priority: returnPath > agentId > /workflows
      const redirectPath = returnPath || (agentId ? `/agents/${agentId}` : '/workflows');

      if (saveDraftResponse.ok) {
        console.log('✅ Draft saved successfully with runId:', runId);

        // Redirect after saving draft
        console.log('🔄 Redirecting to:', redirectPath);
        navigate(redirectPath);
      } else {
        console.warn('⚠️ Failed to save draft');
        // Still redirect even if save failed
        navigate(redirectPath);
      }
    } catch (error) {
      console.error('❌ Failed to save draft:', error);
      // Still redirect even if there's an error
      const redirectPath = returnPath || (agentId ? `/agents/${agentId}` : '/workflows');
      navigate(redirectPath);
    }

    // Close the modal
    setShowBillModal(null);
    console.log('✅ Modal closed, draft saved. Redirecting to:', returnPath || (agentId ? `/agents/${agentId}` : '/workflows'));
  };

  const handleModifyBill = (modifiedData) => {
    console.log('Modified bill data:', modifiedData);
    // Could implement modification logic here if needed
  };

  // ✅ REMOVED normalizeBillData - Backend now sends FLAT structure directly
  // No need for data normalization as component_definition contains bill data at root level

  const renderToolMessage = (content, type) => {
    const lines = content.split('\\n');
    const parts = [];
    let isJsonSection = false;
    let jsonContent = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check if this is the start of a JSON section (after "Parameters:" or "Response:")
      if (line === 'Parameters:' || line === 'Response:') {
        // Add the label
        parts.push(
          <div key={i} className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-2 mb-1">
            {line}
          </div>
        );
        isJsonSection = true;
        jsonContent = '';
        continue;
      }

      // If we're in a JSON section, collect the content
      if (isJsonSection) {
        if (line.trim() === '' && jsonContent === '') {
          continue; // Skip empty lines at the start of JSON
        }

        if (line.startsWith('Executing:') || line.startsWith('Completed:')) {
          // We've hit the next section, render the collected JSON
          if (jsonContent.trim()) {
            // Try to parse JSON to extract netsuite_url
            let netsuiteUrl = null;
            try {
              const parsedJson = JSON.parse(jsonContent.trim());
              netsuiteUrl = parsedJson.netsuite_url || parsedJson.bill_url || parsedJson.netsuite_bill_url;
            } catch (e) {
              // If parsing fails, just show as text
            }

            parts.push(
              <div key={`json-${i}`} className="mt-1">
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 font-mono text-sm overflow-x-auto">
                  <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{jsonContent.trim()}</pre>
                </div>
                {netsuiteUrl && (
                  <div className="mt-2">
                    <a
                      href={netsuiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md shadow-sm transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View Bill in NetSuite
                    </a>
                  </div>
                )}
              </div>
            );
          }
          isJsonSection = false;
          jsonContent = '';

          // Process the current line as a header
          parts.push(
            <div key={i} className="font-medium text-gray-900 dark:text-white">
              {line}
            </div>
          );
        } else {
          jsonContent += (jsonContent ? '\\n' : '') + line;
        }
      } else if (line.startsWith('Executing:') || line.startsWith('Completed:')) {
        parts.push(
          <div key={i} className="font-medium text-gray-900 dark:text-white">
            {line}
          </div>
        );
      } else if (line.trim()) {
        parts.push(
          <div key={i} className="text-gray-700 dark:text-gray-300">
            {line}
          </div>
        );
      }
    }

    // Handle any remaining JSON content at the end
    if (isJsonSection && jsonContent.trim()) {
      parts.push(
        <div key="final-json" className="mt-1">
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 font-mono text-sm overflow-x-auto">
            <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{jsonContent.trim()}</pre>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {parts}
      </div>
    );
  };

  const EmptyState = () => (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
          <Bot className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Ready to Execute
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Start a conversation with <strong>{workflowName}</strong> by sending a message below.
        </p>
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center space-x-2 text-blue-700 dark:text-blue-300 text-sm">
            <Zap className="w-4 h-4" />
            <span>Your message will trigger the workflow execution with real-time updates</span>
          </div>
        </div>
      </div>
    </div>
  );

  const ErrorState = () => (
    <div className="p-4 m-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
      <div className="flex items-center space-x-2 text-red-700 dark:text-red-300">
        <AlertTriangle className="w-5 h-5" />
        <div>
          <h4 className="font-medium">Workflow Error</h4>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    </div>
  );

  // ✅ Cleanup: Close modal and abort pending requests on component unmount
  useEffect(() => {
    return () => {
      if (showBillModal) {
        console.log('🧹 V2ChatMessages unmounting - cleaning up bill modal');
        setShowBillModal(null);
      }
    };
  }, [showBillModal]);

  if (messages.length === 0 && !isLoading && !error) {
    console.log("🪴 Rendering EmptyState (no messages, not loading, no error)");
    return <EmptyState />;
  }


  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900 scrollbar-thin">
      {/* Error display */}
      {error && <ErrorState />}

      {/* Messages */}
      {messages
        .filter((message, index, allMessages) => {
          const content = typeof message.content === 'string' ? message.content : JSON.stringify(message.content);

          // Hide UI component messages with status "ui_ready" (they trigger modals but shouldn't be displayed)
          // This filters the long JSON message from display while still allowing the modal to work
          if (message.isUIComponent) {
            console.log('🙈 Hiding UI component message from display');
            return false;
          }

          // Check if message content contains ui_ready status with component_definition
          if (typeof message.content === 'object' && message.content !== null) {
            if (message.content.status === 'ui_ready' && message.content.component_definition) {
              console.log('🙈 Hiding ui_ready component message from display');
              return false;
            }
          }

          // Also check string content for ui_ready messages
          if (typeof message.content === 'string') {
            try {
              // Try to parse if it looks like JSON
              if (message.content.trim().startsWith('{')) {
                const parsed = JSON.parse(message.content);
                if (parsed.status === 'ui_ready' && parsed.component_definition) {
                  console.log('🙈 Hiding ui_ready string message from display');
                  return false;
                }
              }
            } catch (e) {
              // Not JSON, continue with other filters
            }
          }

          // Filter out technical messages that users shouldn't see
          const technicalPatterns = [
            'Bill confirmed for creation with the following data:',
            '"next_action_required":"process_po_bill_step"',
            '"next_action_required":"standalone_item_mapping_step"',
            '"next_action_required":"po_item_mapping_step"',
            '"next_action_required":"confirm_bill_creation"',
            '"next_action":"confirm_bill_creation"'
          ];

          // Check if this is a technical message
          if (technicalPatterns.some(pattern => content.includes(pattern))) {
            return false;
          }

          // Enhanced filter: Hide messages with next_action_required but no message_for_user
          // These are technical workflow routing messages that shouldn't be shown to users
          if (typeof message.content === 'object' && message.content !== null) {
            const hasNextAction = message.content.next_action_required || message.content.next_action;
            const hasUserMessage = message.content.message_for_user;

            // If it has next_action but no message_for_user (or null), hide it
            if (hasNextAction && !hasUserMessage) {
              console.log('🙈 Hiding technical workflow message (next_action without message_for_user)');
              return false;
            }
          }

          // Also check for stringified messages
          if (typeof message.content === 'string' && message.content.includes('next_action_required')) {
            try {
              const parsed = JSON.parse(message.content);
              const hasNextAction = parsed.next_action_required || parsed.next_action;
              const hasUserMessage = parsed.message_for_user;

              if (hasNextAction && !hasUserMessage) {
                console.log('🙈 Hiding technical workflow message (stringified next_action without message_for_user)');
                return false;
              }
            } catch (e) {
              // Not JSON, continue
            }
          }

          // Filter out raw JSON messages that only contain next_action_required
          if (message.type === 'user' && typeof message.content === 'string') {
            try {
              const parsed = JSON.parse(message.content);
              if (parsed.next_action_required && !parsed.message_for_user) {
                return false; // Hide technical routing messages
              }
            } catch (e) {
              // Not JSON, keep it
            }
          }

          // For bill creation success: Keep only the message with Bill ID, hide generic success messages
          if (message.type === 'assistant' && typeof message.content === 'string') {
            const isBillCreationMsg = message.content.match(/(?:Bill.*created|Bill.*successfully)/i);
            if (isBillCreationMsg) {
              const hasBillId = message.content.match(/(?:Bill.*ID|netsuite_bill_id)[:\s]*([A-Z0-9\-]+)/i);

              // If this message has a Bill ID, keep it
              if (hasBillId) {
                return true;
              }

              // If this message doesn't have a Bill ID, check if there's another message with ID
              const hasMessageWithId = allMessages.some((m, i) =>
                i !== index &&
                m.type === 'assistant' &&
                typeof m.content === 'string' &&
                m.content.match(/(?:Bill.*created|Bill.*successfully)/i) &&
                m.content.match(/(?:Bill.*ID|netsuite_bill_id)[:\s]*([A-Z0-9\-]+)/i)
              );

              // If there's a message with ID, hide this generic one
              if (hasMessageWithId) {
                return false;
              }
            }
          }

          // For errors: Remove duplicate error messages with same content
          if (message.type === 'assistant' && typeof message.content === 'string') {
            const isError = message.content.match(/(?:error|failed|unable|invalid)/i);
            if (isError) {
              // Check if there's an earlier message with same or similar error
              const hasDuplicateError = allMessages.some((m, i) =>
                i < index &&
                m.type === 'assistant' &&
                typeof m.content === 'string' &&
                m.content.toLowerCase().includes(message.content.toLowerCase().substring(0, 50))
              );

              if (hasDuplicateError) {
                return false; // Hide duplicate error
              }
            }
          }

          // ✅ IMPORTANT: Show search and item mapping tool messages to user
          // These messages show the NetSuite item search results and are important for transparency:
          // - get_netsuite_item_details: Shows items found for each line item
          // - search_netsuite_item_by_name: Shows targeted search results
          // - lookup_memorized_item: Shows if item mapping was remembered
          // ❌ ONLY hide get_all_netsuite_items (too large - entire catalog)

          if ((message.type === 'tool_call' || message.type === 'tool_result') &&
              message.tool_name === 'get_all_netsuite_items') {
            console.log('🙈 Hiding get_all_netsuite_items message (entire catalog - too large)');
            return false; // Hide this tool message
          }

          // Also check if the content contains get_all_netsuite_items (for different message formats)
          if (typeof message.content === 'string' &&
              (message.content.includes('Executing: get_all_netsuite_items') ||
               message.content.includes('Completed: get_all_netsuite_items'))) {
            console.log('🙈 Hiding get_all_netsuite_items by content match');
            return false;
          }

          return true;
        })
        .map((message, index) => {
        console.log(`📝 Rendering message[${index}]`, {
          id: message?.id,
          type: message?.type,
          contentType: typeof message?.content,
          preview: typeof message?.content === "string"
            ? message.content.slice(0, 100)
            : message?.content
        });
        const styles = getMessageStyles(message.type);


        return (
          <div key={message.id} className={`flex ${styles.container}`}>
            <div className="flex items-start space-x-3 max-w-4xl">
              {/* Avatar */}
              {message.type !== 'user' && message.type !== 'system' && (
                <div className={`flex-shrink-0 p-2 rounded-full ${styles.icon}`}>
                  {getMessageIcon(message.type)}
                </div>
              )}

              {/* Message bubble */}
              <div className={`rounded-lg px-4 py-3 ${styles.bubble} shadow-sm`}>

                {/* Message content */}
                {renderMessageContent(message.content, message.type, message)}

                {/* Message metadata */}
                <div className="flex items-center justify-between mt-2 text-xs opacity-70">
                  <span>{formatTimestamp(message.timestamp)}</span>
                  <div className="flex items-center space-x-2">
                    {/* Tokens used */}
                    {message.metadata?.tokens_used && (
                      <span className="flex items-center space-x-1">
                        <Zap className="w-3 h-3" />
                        <span>{message.metadata.tokens_used} tokens</span>
                      </span>
                    )}
                    {/* Tool name for tool messages */}
                    {message.tool_name && (message.type === 'tool_call' || message.type === 'tool_result') && (
                      <span className="bg-black/10 dark:bg-white/10 px-2 py-1 rounded text-xs">
                        {message.tool_name}
                      </span>
                    )}
                    {/* Step name */}
                    {message.metadata?.step_name && (
                      <span className="bg-black/10 dark:bg-white/10 px-2 py-1 rounded text-xs">
                        {message.metadata.step_name}
                      </span>
                    )}
                    {/* Execution time */}
                    {message.metadata?.execution_time_ms && (
                      <span className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{message.metadata.execution_time_ms}ms</span>
                      </span>
                    )}
                    {/* Auto-close indicator for workflow completion */}
                    {(() => {
                      if (!showBillModal || (message.type !== 'assistant' && message.type !== 'tool_result')) return null;

                      // Check for success
                      const successPatterns = [
                        /(?:Bill.*created|Bill.*successfully|NetSuite.*Bill.*ID)/i,
                        /Successfully created Vendor Bill/i,
                        /netsuite_bill_id.*\d+/i,
                        /status.*success.*created/i,
                        /Workflow completed successfully/i
                      ];

                      if (typeof message.content === 'string' && successPatterns.some(pattern => message.content.match(pattern))) {
                        return (
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            ✨ Modal closing in 3s
                          </span>
                        );
                      }

                      // Check for errors
                      const errorPatterns = [
                        /bill.*creation.*failed/i,
                        /bill.*failed/i,
                        /error.*creating.*bill/i,
                        /netsuite.*api.*error/i,
                        /purchase.*order.*not.*found/i,
                        /unable.*to.*proceed/i,
                        /connectivity.*issue/i,
                        /unknown.*error/i
                      ];

                      if (typeof message.content === 'string' && errorPatterns.some(pattern => message.content.match(pattern))) {
                        return (
                          <span className="text-red-600 dark:text-red-400 font-medium">
                            ❌ Modal closing in 5s
                          </span>
                        );
                      }

                      // Check for JSON error responses
                      try {
                        const parsedContent = typeof message.content === 'object' ? message.content : (typeof message.content === 'string' && message.content.includes('{"error"') ? JSON.parse(message.content) : null);
                        if (parsedContent && (parsedContent.error || parsedContent.message_for_user?.includes('failed') || parsedContent.next_action_required === 'bill_creation_failed')) {
                          return (
                            <span className="text-red-600 dark:text-red-400 font-medium">
                              ❌ Modal closing in 5s
                            </span>
                          );
                        }
                      } catch (e) {
                        // Not valid JSON, ignore
                      }

                      // Check for workflow completion steps
                      const completionSteps = [
                        'final_output_step',
                        'halt_workflow_bill_creation_error',
                        'halt_workflow_ai_error_step',
                        'halt_workflow_netsuite_search_error_step'
                      ];

                      if (message.metadata?.step_name && completionSteps.includes(message.metadata.step_name)) {
                        return (
                          <span className="text-blue-600 dark:text-blue-400 font-medium">
                            🏁 Modal closing in 4s
                          </span>
                        );
                      }

                      return null;
                    })()}
                  </div>
                </div>
              </div>

              {/* User avatar */}
              {message.type === 'user' && (
                <div className={`flex-shrink-0 p-2 rounded-full ${styles.icon}`}>
                  {getMessageIcon(message.type)}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Show only ONE indicator at a time - priority: isResuming > isProcessing > isLoading */}

      {/* Resuming indicator - shows when user clicks Resume button */}
      {isResuming && !isProcessing && (
        <div className="flex justify-start">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400">
              <Bot className="w-4 h-4" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 border border-green-200 dark:border-green-700 shadow-sm">
              <div className="flex items-center space-x-3">
                {/* Old: <SpinningSankalvaXLogo size="w-5 h-5" className="text-green-600 dark:text-green-400" showBackground={true} /> */}
                <SpinningGruveLogo size="w-5 h-5" className="text-green-600 dark:text-green-400" showBackground={true} />
                <span className="text-sm text-gray-700 dark:text-gray-300">Resuming workflow...</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Processing indicator - shows when workflow is executing after resume */}
      {isProcessing && (
        <div className="flex justify-start">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400">
              <Bot className="w-4 h-4" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 border border-purple-200 dark:border-purple-700 shadow-sm">
              <div className="flex items-center space-x-3">
                {/* Old: <SpinningSankalvaXLogo size="w-5 h-5" className="text-purple-600 dark:text-purple-400" showBackground={true} /> */}
                <SpinningGruveLogo size="w-5 h-5" className="text-purple-600 dark:text-purple-400" showBackground={true} />
                <span className="text-sm text-gray-700 dark:text-gray-300">Processing workflow...</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading indicator - only show if NOT resuming or processing */}
      {isLoading && !isResuming && !isProcessing && (
        <div className="flex justify-start">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
              <Bot className="w-4 h-4" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-center">
                {/* Old: <SpinningSankalvaXLogo size="w-5 h-5" className="text-blue-600 dark:text-blue-400" showBackground={true} /> */}
                <SpinningGruveLogo size="w-5 h-5" className="text-blue-600 dark:text-blue-400" showBackground={true} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />

      {/* Bill Confirmation Modal */}
      {showBillModal && (
        <PreBillCreationModal
          billData={showBillModal}
          onConfirm={handleConfirmBill}
          onCancel={handleCancelBill}
          onClose={handleCloseBill}
          onModify={handleModifyBill}
          token={token}
          workflowContext={workflowContext || { workflowId, runId }}
          runId={runId}
          workflowId={workflowId}
          chatService={chatService}
        />
      )}

      {/* Debug modal state */}
      {console.log('🎯 Modal render check - showBillModal:', showBillModal)}
    </div>
  );
};

export default V2ChatMessages;
